/* eslint-disable class-methods-use-this */
import { v4 as uuid } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import map from 'lodash/map';
import find from 'lodash/find';
import each from 'lodash/each';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import store from '@Store';
import { SearchTypes, RxSocketUrls, socketConfig } from '@Constants/CommonConstants';
import { config } from '@Constants/rx-ui/constants';
import PriorityWatcher from '@Services/PriorityWatcher';
import QueryParser from '@Services/QueryParser';
import ResultsInfo from '@Services/ResultsInfo';
import Similarity from '@Services/Similarity';
import Search from '@Services/Search';
import AppStorage from '@Services/AppStorage';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import UserDatabases from '@Services/UserDatabases';
import History from '@Services/History';
import HistoryHelper from '@Services/HistoryHelper';
import Catalyst from '@Services/Catalyst';
import CatalystHelper from '@Services/CatalystHelper';
import Entries from '@Services/Entries';
import Breadcrumb from '@Services/Breadcrumb';
import SearchStorage from '@Services/SearchStorage';
import SocketBroker from '@Services/SocketBroker';
import * as FormatUtils from '@Services/FormatUtils';
import * as AdvancedQuery from '@Services/AdvancedQuery';
import * as QueryValidation from '@Services/QueryValidation';
import {
  dispatchCloseSearchProgressModal,
  dispatchOpenSearchProgressModal,
  dispatchUpdateStateParams,
} from '@Containers/Search/Shared/SearchProgress/SearchProgressModalActions';
import { updateCasNumberFromSearchPayload } from '@Containers/CasNumber/actions';
import { generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';

/* eslint-disable lines-between-class-members */

class HeaderContainerService {
  _priorityWatcher;
  databaseIds;
  searchDb;
  isSearchFinished;
  isSimilarMode;
  unsubscribeSockets;
  searchId;
  requestId;
  queryString;
  request;
  isLastSearchZero;
  nodesList;
  properties;

  init() {
    this._priorityWatcher = new PriorityWatcher();
    this.databaseIds = UserDatabases.getListWithIds();
    this.searchDb = AppStorage.instance.get('qbSearchDB');
    this.isSearchFinished = false;
    this.isSimilarMode = false;
    this.unsubscribeSockets = noop;
    this.searchId = SearchStorage.uniqId();
    this.requestId = uuid();
    this.queryString = {};
    this.request = {};
    this.isLastSearchZero = false;
    this.nodesList = null;
    this.properties = null;
    this.skipNextBracketsQuotesCheck = false;
  }

  showInvalidQueryMessage = () => {
    const errorTitle = 'Query is invalid';
    const errorMessage = 'Make sure you entered search params';
    NotifyDialogsService.openModalWindow({
      title: errorTitle,
      text: errorMessage,
      type: 'error',
      confirmText: 'Close',
      trackingOptions: {
        message: `${errorTitle} ${errorMessage}`,
        component: 'Advanced search',
      },
    });
  };

  showInvalidHopintoSearchParamMessage = () => {
    NotifyDialogsService.openModalWindow({
      title: 'Error',
      text: 'Application cannot restore search query from HopInto request parameters',
      type: 'error',
      confirmText: 'Continue',
      timestamp: new Date().toGMTString(),
      trackingOptions: {
        component: 'Advanced search',
      },
    });
  };

  showWSErrorMessage = (error) => {
    const errorTitle = 'Query is invalid';
    const errorMessage = error?.exception?.message || 'Make sure you entered search params';
    NotifyDialogsService.openModalWindow({
      title: errorTitle,
      text: errorMessage,
      type: 'error',
      confirmText: 'Close',
      trackingOptions: {
        message: `${errorTitle} ${errorMessage}`,
        component: 'Advanced search',
      },
    });
  };

  showEmptyResultMessage = (request) => {
    NotifyDialogsService.openModalWindow({
      title: 'No results',
      text: "Reaxys didn't find results for your query. Please consider reformulation and try again.",
      type: 'error',
      confirmText: 'OK',
      backdrop: 'static',
      timestamp: new Date().toGMTString(),
      crumb: request,
      trackingOptions: {
        component: 'Advanced search',
      },
    });
  };

  isSimilaritySearch = (queryString) => (JSON.stringify(queryString).match(/"value":"similarity"/g) || []).length === 1;

  openSimilarityModal = (result, context, properties) => {
    // RXFE-3432: This is required for proper ordering of databases in similarity pop-up
    const databasesQB = properties?.filter((item) => item.type === 'database').map((item) => item.displayName);
    Similarity.showModalForQueryBuilder(this.searchId, result, context, databasesQB)
      .then((modalResult) => {
        AppStorage.instance.set('historyOfVisitedDbSimilarity', [modalResult.selectedResult]);
      })
      .catch(() => {
        if (this.searchId) {
          Search.cancelSearch(this.searchId, this.requestId);
        }
      });
  };

  handleSimilarityResults(result) {
    if (!this.isSimilarMode) {
      const firstDbItem = find(result.payload.items, (item) => !!item.databaseId);
      // eslint-disable-next-line no-param-reassign
      result.databaseId = firstDbItem ? firstDbItem.databaseId : null;
      const context = firstDbItem ? firstDbItem.context : null;
      this.isSimilarMode = true;
      this.openSimilarityModal(result, context, this.properties, this.searchId, this.requestId);
    }

    if (result && result.resultName && this.searchDb && this.nodesList?.length > 0) {
      // eslint-disable-next-line no-param-reassign
      result.payload.query = result.query;
      // eslint-disable-next-line no-param-reassign
      result.payload.queryFactItem = this.queryString;
      Similarity.updateObserver(result);
    }
    Similarity.updateLocalSearch(result, result.payload?.searchId);
  }

  initResultsInfo(result) {
    const searchQueries = {};
    each(result.payload.items, (item) => {
      searchQueries[item.databaseId] = item.queryId;
      dispatchUpdateStateParams({
        searchId: result.payload?.searchId,
        _priorityWatcher: this._priorityWatcher,
        queries: searchQueries,
        requestId: this.requestId,
      });
    });

    this._priorityWatcher.init(result.payload.items, 'queryId');

    ResultsInfo.startLoading(
      map(result.payload.items, (item) => ({
        databaseId: item.databaseId,
        context: item.context,
        queryId: item.queryId,
        groupId: item.groupId,
        isFromAdvancedSearch: 1,
      }))
    );
  }

  updateResultsInfo(result) {
    this._priorityWatcher.addItem(result, 'payload.queryId');

    const searchId = result?.payload?.searchId;
    const textQuery = result?.payload?.textQuery;

    if (searchId && textQuery) {
      const semanticQueryBySearchId = AppStorage.instance.getFromSessionStorage('qbSemanticQueryBySearchId') || {};
      AppStorage.instance.setToSessionStorage('qbSemanticQueryBySearchId', {
        ...semanticQueryBySearchId,
        [searchId]: textQuery,
      });
    }

    const patch = {
      resultsCount: result.resultsCount,
      searchId: result.payload.searchId,
      hitSetKey: result.hitSetKey,
      pageNumber: 1,
      resultName: result.payload.resultName,
      description: null,
      ...(result.resolvedByCasApi && { resolvedByCasApi: result.resolvedByCasApi }),
      recordCount: result.payload.recordCount, // Patent Family Clustering
      ...(result.payload.semanticSearchIncluded && {
        semanticSearchIncluded: result.payload.semanticSearchIncluded,
        semanticResultName: result.payload.semanticResultName,
        textQuery: result.payload.textQuery,
      }),
      ...(result.payload.reactionSummaryEligible !== undefined && { reactionSummaryEligible: result.payload.reactionSummaryEligible }),
    };
    ResultsInfo.updateInfo(patch, result.payload.queryId);
  }

  handleEmptyResults(result) {
    // RXFE-5695: store zero results for integrated databases
    if (result.resultsCount === 0) {
      History.storeRecentZeroSearch(result, 'queryBuilder', this.request.queryFactItem);
    }
    // handling case with zero results for all databases
    if (result.resultsCount === 0 && this._priorityWatcher.isAllZeroCount()) {
      Catalyst.trackSearchResults(
        'searchResultsUpdated',
        {
          criteria: CatalystHelper.getCriteriaFromAdvancedSearch(this.request.queryFactItem, this.request.context),
          dataFormCriteria: CatalystHelper.getDataFormCriteriaFromAdvancedSearch(this.request.queryFactItem, this.request.context),
          resultsPerPage: (Entries.limit || config.NUMBER_OF_PAGE_ITEMS).toString(),
          totalResults: '0',
          ...CatalystHelper.generateTrackedObjFromNodesList(this.nodesList, this.request.context),
        },
        store.getState()
      );
      this.unsubscribeSockets('empty result');
      // Show modal If no items for all results
      this.showEmptyResultMessage(this.request);
      // Triggering history update
      this.isLastSearchZero = true;
      if (history.location.state && history.location.state.autoSearch) {
        history.push(paths.SEARCH);
      }
    }
  }

  showResultsBackendResponse(result) {
    if (this.isSimilarMode) {
      // eslint-disable-next-line no-param-reassign
      result.payload.query = result.query;
      // eslint-disable-next-line no-param-reassign
      result.payload.queryFactItem = this.queryString;
      Similarity.updateObserver(result);
    } else {
      dispatchCloseSearchProgressModal();
      const stateParams = history.location.state;
      const params = {
        databaseId: result.databaseId,
        context: result.context,
        resultName: result.resultName,
        searchId: this.searchId,
        isFromAdvancedSearch: 1,
        hitSetKey: result.hitSetKey,
        hitsPerPage: stateParams ? stateParams.hitsPerPage : null,
        trackedObj: CatalystHelper.generateTrackedObjFromNodesList(this.nodesList, result.context),
        uiDirectives: stateParams ? stateParams.uiDirectives : null,
      };
      const pathName = generateResultsPath(params);
      history.push({
        pathname: pathName,
        state: params,
      });
    }
  }

  showResults(result, groupId) {
    // setSearchFinished(true);
    this.isSearchFinished = true;
    Breadcrumb.resetBreadcrumbs();
    const searchObj = {
      query: result.query,
      queryFactItem: this.queryString,
      searchType: this.isSimilarMode ? SearchTypes.SIMILARITY_SEARCH : SearchTypes.ADVANCED_SEARCH,
    };
    const promise = this.isSimilarMode
      ? Similarity.updateSearch(this.searchId, searchObj, true)
      : SearchStorage.extendItem(this.searchId, searchObj);

    promise
      .then(() => {
        ResultsInfo.commitResultsToBreadcrumbs(groupId)
          .then(() => {
            if (result?.resultName && this.searchDb && this.nodesList.length > 0) {
              // POST the QB search result to the backend recent-history API after the WS call delivers results.
              // Breadcrumbs are now set; grab the active one for the primary database and persist it.
              const activeBreadcrumb = Breadcrumb.getActiveBreadcrumbForDb(result.databaseId);
              if (activeBreadcrumb) {
                HistoryHelper.storeRecentHistory({
                  ...activeBreadcrumb,
                  historyType: 'queryBuilder',
                  queryFactItem: this.request?.queryFactItem,
                });
              }
              this.showResultsBackendResponse(result, groupId);
            }
          })
          .catch(Logger.error);
      })
      .catch(Logger.error);
  }

  handleSearchResults(result) {
    const { payload } = result;

    // eslint-disable-next-line no-param-reassign
    result = {
      context: payload.context,
      databaseId: payload.databaseId,
      resultName: payload.resultName,
      hitSetKey: payload.hitSetKey,
      resultsCount: payload.resultSize,
      payload,
      ...(payload.casRn && { resolvedByCasApi: true }),
    };

    if (!this.searchId || this.searchId.toString() !== result.payload.searchId) {
      return;
    }

    // CAS Search - QB
    updateCasNumberFromSearchPayload(payload);

    // Similarity case
    if (this.isSimilarMode || (result.payload && result.payload.similarity && !result.resultName)) {
      const imgUrl = get(result, 'payload.image.url');

      if (imgUrl) {
        // eslint-disable-next-line no-param-reassign
        result.payload.image.url = FormatUtils.formatPath(imgUrl);
      }
      this.handleSimilarityResults(result);
      return;
    }

    // Handle non similarity heap
    if (isArray(get(result, 'payload.items', null))) {
      this.initResultsInfo(result);
    } else {
      this.updateResultsInfo(result);
    }
    this.handleEmptyResults(result);
    if (this.isSearchFinished) {
      ResultsInfo.delayedResultsInfoSubject.next();
    }

    // Try to get completed result with highest priority
    const highPriorityResult = this._priorityWatcher.getHighPriorityItemResults();
    if (!this.isSearchFinished && highPriorityResult) {
      const highPriorityItem = this._priorityWatcher.getHighPriorityItem();
      this.showResults(highPriorityResult, highPriorityItem.groupId);
    }
    // unsubscribe only all data has come
    if (this.isSearchFinished && this._priorityWatcher.isAllResultsLoaded() && result.resultsCount) {
      this.unsubscribeSockets('handleSearchResult cleanup searchfinished');
    }
  }

  sendViaWS(req) {
    const { queryFactItem } = req;
    // queryFactItem, context: searchDb.serverAlias, databases: DBList, searchId: searchUUID, requestId: reqUUID
    if (!this.isSimilaritySearch(this.queryString)) {
      dispatchOpenSearchProgressModal({
        searchId: this.searchId,
        requestId: this.requestId,
        queries: {},
        _priorityWatcher: this._priorityWatcher,
        queryFactItem,
      });
    }

    const subject = SocketBroker.send(
      {
        send: RxSocketUrls.AdvancedQuery,
        cancel: RxSocketUrls.CancelSearch,
        message: RxSocketUrls.ChannelsUrls.SearchInfo,
      },
      {
        searchId: this.searchId,
        params: req,
      }
    );

    this.unsubscribeSockets = subject.cancel;
    // Also listen for queue errors specifically for this search
    const queueErrorUnsubscriber = SocketBroker.observe(RxSocketUrls.ChannelsUrls.QueueErrors, (data) => {
      if (data?.exception?.statusCode === 400) {
        dispatchCloseSearchProgressModal();
        this.showWSErrorMessage(data);
      }
    });
    subject.subscribe({
      next: ({ data }) => {
        this.handleSearchResults(data);
      },
      error: (error) => {
        dispatchCloseSearchProgressModal();
        queueErrorUnsubscriber();
        if (error.reason === socketConfig.REASON_LIMIT) {
          this.showWSErrorMessage(error);
        }
      },
    });
  }

  searchStructureOptions(DBList) {
    AdvancedQuery.checkStructureOptions(this.queryString)
      .then((queryFactItem) => {
        this.requestId = uuid();
        this.request = {
          queryFactItem,
          context: this.searchDb?.serverAlias,
          databases: DBList,
          searchId: this.searchId,
          requestId: this.requestId,
        };
        if (history.location.state && isString(history.location.state.autoSearch)) {
          // hopInto param for stats on BE (RXFE-5944)
          this.request[history.location.state.autoSearch] = true;
        }
        Catalyst.updateSearchObject({
          details: QueryValidation.validateIsPhraseSearch(this.request.queryFactItem) ? 'phrase:true' : null,
        });

        this.sendViaWS(this.request);
        Logger.info('WS QB request:', this.request);
      })
      .catch(Logger.error);
  }

  validateQueryAndProceed(searchContext) {
    if (!QueryValidation.validateBrackets(this.queryString) || !QueryValidation.validateQuotes(this.queryString)) {
      QueryValidation.showIncorrectBracketsOrQuotesMessage({
        onConfirm: () => {
          this.skipNextBracketsQuotesCheck = true;
          this.searchInDb(searchContext, this.nodesList, this.properties, true);
        },
      });
      return false;
    }
    return true;
  }

  _getSanitizedDBList() {
    // get list of databases for which history items were added to query builder
    let DBList = AdvancedQuery.getDBList();

    // Update db in case search is requested with a specific database
    // so far specific to hopinto workflow
    // DB is provided in query then search should happen only in that DB
    const param = history.location.state;
    const userSelectedDB = param?.database || null;
    if (param?.autoSearch?.toLowerCase() === 'hopinto' && userSelectedDB) {
      const userSelectedDBId = UserDatabases.getDatabaseIdByPrefix(userSelectedDB);
      if (!userSelectedDBId) {
        // user session context did not have a DB with searched prefix
        // Show modal If db prefix is not found in the user context
        this.showInvalidHopintoSearchParamMessage();
        return null; // Signals failure
      }
      DBList = [userSelectedDBId];
    }

    // if there are no history items, use default list of databases
    if (isEmpty(DBList)) {
      DBList = this.databaseIds;
    }
    return DBList;
  }

  searchInDb(searchContext, nodesList, properties) {
    this.nodesList = nodesList;
    this.properties = properties;

    this._priorityWatcher.clear();

    this.searchDb = searchContext || AppStorage.instance.get('qbSearchDB');
    AppStorage.instance.set('qbSearchDB', this.searchDb);

    if (!this.searchDb || !this.nodesList.length) {
      return;
    }

    QueryParser.setRemoveEmptyOperators(true);
    this.queryString = QueryParser.stringifyQuery(this.nodesList);

    if (!AdvancedQuery.isValidQuery(this.queryString)) {
      this.showInvalidQueryMessage();
      return;
    }

    // Validate language
    if (!QueryValidation.validateLanguage(this.queryString)) {
      QueryValidation.showIncorrectLanguageMessage();
      return;
    }

    // Validate brackets and quotes
    if (!this.skipNextBracketsQuotesCheck) {
      if (!this.validateQueryAndProceed(searchContext)) {
        return;
      }
    } else {
      this.skipNextBracketsQuotesCheck = false;
    }

    const DBList = this._getSanitizedDBList();

    if (!DBList) {
      return;
    }

    // RXFE-4513: remove facts without constraints
    AdvancedQuery.removeFactsWithoutConstraints(this.queryString);
    // RXFE-7716:
    // if user enters two double quotes next to each other, then automatically insert a space between them,
    // before it is being send to the backend services
    QueryValidation.insertSpaceBetweenAdjacentQuotes(this.queryString);
    this.searchStructureOptions(DBList);
  }
}

export default new HeaderContainerService();
